import dayjs from "dayjs";
import * as echarts from "echarts";
import { useEffect, useRef } from "react";
import { darkTheme } from "./dark";
import { formatter } from "./utils-usage";
import { UsageGraphProps } from "./types";
import { createCreditAmountSeries, formatDate } from "./usage-graph-utils";

export const useChartConfig = (props: UsageGraphProps, darkMode: boolean) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.isSuccess && chartRef.current) {
      let chartInstance =
        echarts.getInstanceByDom(chartRef.current) ||
        echarts.init(chartRef.current, darkMode ? darkTheme : undefined);

      const option = createChartOption(props, darkMode);
      chartInstance.setOption(option, true);

      const ro = new ResizeObserver(() => {
        chartInstance.resize();
      });
      ro.observe(chartRef.current);

      return () => {
        ro.disconnect();
        chartInstance.dispose();
      };
    }
  }, [props.isSuccess, props.endTime, props.startTime, darkMode]);

  return chartRef;
};

const createChartOption = (props: UsageGraphProps, darkMode: boolean) => {
  const { usageQuery, hideLegend, creditAmount } = props;
  const { data } = usageQuery;

  const showTitle =
    data?.appCreditsData.series.length === 0 ||
    props.isLoading ||
    props.isError;
  const showData =
    data &&
    data.appCreditsData.series.length > 0 &&
    !props.isLoading &&
    !props.isError;

  const titleText = props.isLoading
    ? "Loading the usage data"
    : props.isError
    ? "An error occurred while loading data"
    : "No usage data";

  return {
    title: {
      show: showTitle,
      textStyle: { color: "grey", fontSize: 20 },
      text: titleText,
      left: "center",
      top: "center",
    },
    tooltip: {
      renderMode: "html",
      appendToBody: true,
      trigger: "axis",
      //@ts-ignore
      formatter: (params: any) => formatter(params, data.intervalHours),
    },
    legend: hideLegend
      ? undefined
      : {
          type: "scroll",
          orient: "horizontal",
          bottom: 0,
          left: 0,
          data: data?.appNames,
        },
    xAxis: {
      show: showData,
      type: "category",
      data: data?.appCreditsData.dataTime.map((dateStr) =>
        formatDate(dateStr, data.intervalHours)
      ),
    },
    yAxis: {
      show: showData,
      max: creditAmount ? Math.floor(creditAmount * 1.1) : undefined,
    },
    series: creditAmount
      ? //@ts-ignore
        createCreditAmountSeries(data, creditAmount)
      : data?.appCreditsData.series.map((s) => ({
          ...s,
          data: maskFutureData(s.data, data.appCreditsData.dataTime),
        })),
  };
};

// Replace values for buckets whose start time is after "now" with null so
// echarts renders them as gaps rather than zeros. The bucket start is the
// timestamp stored in dataTime; we compare against "now" so that the current
// in-progress bucket stays rendered.
const maskFutureData = (
  data: number[],
  dataTime: string[] | undefined,
): (number | null)[] => {
  if (!dataTime || dataTime.length !== data.length) return data;
  const now = dayjs();
  return data.map((value, i) => {
    const bucketStart = dayjs(dataTime[i]);
    if (!bucketStart.isValid()) return value;
    return bucketStart.isAfter(now) ? null : value;
  });
};
